// @/store/notificationSlice.ts
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from './store'

export type NotificationType = 'error' | 'success' | 'warning' | 'info'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  description?: string
  timestamp: number
  duration?: number // Auto-dismiss duration in ms
  displayed?: boolean // Track if already shown
}

export interface NotificationState {
  queue: Notification[]
}

const initialState: NotificationState = {
  queue: [],
}

let notificationCounter = 0

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    pushNotification: (
      state,
      action: PayloadAction<
        Omit<Notification, 'id' | 'timestamp' | 'displayed'>
      >
    ) => {
      const notification: Notification = {
        ...action.payload,
        id: `notification-${Date.now()}-${++notificationCounter}`,
        timestamp: Date.now(),
        displayed: false,
      }
      state.queue.push(notification)
    },

    markAsDisplayed: (state, action: PayloadAction<string>) => {
      const notification = state.queue.find((n) => n.id === action.payload)
      if (notification) {
        notification.displayed = true
      }
    },

    removeNotification: (state, action: PayloadAction<string>) => {
      state.queue = state.queue.filter((n) => n.id !== action.payload)
    },

    clearAllNotifications: (state) => {
      state.queue = []
    },
  },
})

export const {
  pushNotification,
  markAsDisplayed,
  removeNotification,
  clearAllNotifications,
} = notificationSlice.actions

export const notificationReducer = notificationSlice.reducer

// Selectors
export const selectNotifications = (state: RootState) =>
  state.notification.queue

export const selectPendingNotifications = createSelector(
  [selectNotifications],
  (queue) => queue.filter((n) => !n.displayed)
)
